import { useRouter } from 'next/navigation';
import { useCallback, useTransition } from 'react';
import type { MaybePromise } from '@/type-utils';

/** The options that can be passed to a `router.push()`. */
type NavigationOptions = Parameters<ReturnType<typeof useRouter>['push']>[1];
/** A `push`-like function that you'd get from {@link useRouter}. */
// This definition doesn't use `useRouter` to avoid a strange Cmd-click behavior in VSCode.
type PushNavigationFn = (href: string, options?: NavigationOptions) => void;

/**
 * A utility hook that coordinates an action with a push navigation in
 * a React transition. This allows us to synchronize state updates within
 * the action with the navigation.
 *
 * @param action - The action to perform alongside the navigation. Generally,
 * this should update some state that needs to be synchronized with navigation.
 * @returns A tuple containing a `push`-like navigation function and a boolean
 * indicating whether the navigation is pending.
 */
export function useActionWithPush(
  action: () => MaybePromise<void>
): [PushNavigationFn, boolean] {
  const router = useRouter();
  const [isRoutePending, startRouteTransition] = useTransition();

  const wrappedAction = useCallback<PushNavigationFn>(
    (href, options) => {
      startRouteTransition(async () => {
        router.push(href, options);
        await action();
      });
    },
    [action, router]
  );

  return [wrappedAction, isRoutePending];
}
